import { useEffect, useState } from "react";
import { account, mailSent, sentWhen, serverFeatures } from "../account.ts";
import { useStore } from "../hooks.ts";
import { messageOf } from "./Login.tsx";

const LATER_KEY = "lagerlog.confirm-later";
const LATER_MS = 7 * 86_400_000;

function putOff(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(LATER_KEY) ?? 0) < LATER_MS;
  } catch {
    return false;
  }
}

/**
 * „Bitte bestätige deine E-Mail-Adresse“ (Martin, 25.10.2026): addresses from before count as
 * unconfirmed, and only a confirmed one gets a link for a new password. Shown on an account's
 * device where the app sends mail, with a button for the link; „Später“ hides it for 7 days on
 * this device. „Mein Konto“ shows the state itself. A mail sent in the last 24 hours (after signing
 * up, say) is named instead of offering another at once (Martin, 05.11.2026: two mails after
 * signing up); sending again stays possible as a small link.
 */
const RECENT_MS = 24 * 3600_000;
export function ConfirmBanner({ route }: { route: string }) {
  const { store } = useStore();
  const [waiting, setWaiting] = useState<{ email: string; sentAt: number | null } | null>(null);
  const [later, setLater] = useState(putOff);
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const accountDevice = !!store.config && !store.config.token;

  useEffect(() => {
    if (!accountDevice || later) return;
    let gone = false;
    Promise.all([serverFeatures(), account.me()]).then(
      ([f, me]) => {
        const email = me.user.emailPending ?? (me.user.email && !me.user.emailConfirmed ? me.user.email : null);
        if (!gone) setWaiting(f.mail && !me.demo && email ? { email, sentAt: me.emailSentAt ?? null } : null);
      },
      () => undefined,
    );
    return () => {
      gone = true;
    };
  }, [accountDevice, later]);

  if (!waiting || later || route === "konto") return null;
  async function send() {
    setState({ busy: true });
    try {
      const { email } = await account.emailAgain();
      setState({ ok: mailSent(email) });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  function postpone() {
    try {
      localStorage.setItem(LATER_KEY, String(Date.now()));
    } catch {
      // Without storage it comes back on the next start.
    }
    setLater(true);
  }
  const recent = waiting.sentAt !== null && Date.now() - waiting.sentAt < RECENT_MS;
  const text = recent
    ? `Wir haben dir ${sentWhen(waiting.sentAt!)} eine Mail an ${waiting.email} geschickt. Tipp auf den Link darin, um deine Adresse zu bestätigen.`
    : "Bitte bestätige deine E-Mail-Adresse. Dann kannst du dein Passwort selbst zurücksetzen.";
  return (
    <div className="banner confirm-banner" data-testid="confirm-banner">
      <span>{state.ok ?? state.error ?? text}</span>
      {!state.ok && (
        <span className="buttons">
          <button className={recent ? "link" : ""} disabled={state.busy} onClick={send} data-testid="confirm-send">
            {recent ? "Keine Mail bekommen? Nochmal schicken" : "Mail schicken"}
          </button>
          <button className="link" onClick={postpone} data-testid="confirm-later">
            Später
          </button>
        </span>
      )}
    </div>
  );
}
